import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { CampaignTypeModal } from '../components/CampaignTypeModal';
import { OfficialApiCampaignModal } from '../components/OfficialApiCampaignModal';
import { OfficialApiCampaignReport } from '../components/OfficialApiCampaignReport';
import { BotAgenteSelector } from '../components/BotAgenteSelector';
import { useTheme } from '../contexts/ThemeContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageActions } from '../contexts/PageActionsContext';
import { apiService } from '../services/api';
import { exportToPdf } from '../utils/pdfExport';
import { Etiqueta } from '../types';
import { TooltipHelp } from '../components/TooltipHelp';

type MessageContent =
  | { text: string }
  | { url: string; caption?: string }
  | { url: string; fileName?: string }
  | { sequence: Array<{ type: string; content: any }> };

interface Campaign {
  id: string;
  nome: string;
  campaignType: 'WHATS_WEB' | 'OFFICIAL_API';
  targetTags: string[];
  sessionNames: string[];
  sessionName: string;
  messageType: string;
  messageContent: MessageContent;
  randomDelay: number;
  startImmediately: boolean;
  scheduledFor: string | null;
  status: string;
  totalContacts: number;
  sentCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  criadoEm: string;
  session: {
    name: string;
    displayName?: string;
    status: string;
    mePushName: string | null;
  };
  _count: {
    messages: number;
  };
}

interface WhatsAppSession {
  name: string; // Nome real usado na API
  displayName?: string; // Nome exibido ao usuário
  mePushName: string | null;
  meId: string | null;
}

interface ContactTag {
  id: string;
  nome: string;
}

export function CampaignsPage() {
  const { theme } = useTheme();
  const { canUseWhatsWeb, canUseOfficialApi, selectedTenantId } = useTenant();
  const { isUrlLogin } = useAuth();
  const { registerActions, clearActions, registerMetadata } = usePageActions();
  const whatsWebReportRef = useRef<HTMLDivElement>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCampaignTypeModal, setShowCampaignTypeModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOfficialApiModal, setShowOfficialApiModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [currentReportCampaignId, setCurrentReportCampaignId] = useState<string | null>(null);
  const [currentReportCampaignType, setCurrentReportCampaignType] = useState<'WHATS_WEB' | 'OFFICIAL_API'>('WHATS_WEB');
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportItemsPerPage] = useState(8);
  const [campaignsCurrentPage, setCampaignsCurrentPage] = useState(1);
  const [campaignsPerPage] = useState(20); // Aumentado de 10 para 20
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalSentDispatches, setTotalSentDispatches] = useState<number | null>(null);
  const [dispatchLimit, setDispatchLimit] = useState<number | null>(null);
  const [dispatchPercentage, setDispatchPercentage] = useState<number>(0);
  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [selectedEtiquetas, setSelectedEtiquetas] = useState<Etiqueta[]>([]);
  const [loadingEtiquetas, setLoadingEtiquetas] = useState(false);
  const [whatsappSessions, setWhatsappSessions] = useState<WhatsAppSession[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: number]: boolean }>({});
  const [fileInfos, setFileInfos] = useState<{ [key: number]: { name: string, size: number, type: string } }>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const [campaignToToggle, setCampaignToToggle] = useState<{ id: string, action: 'pause' | 'resume' } | null>(null);
  const [togglingCampaign, setTogglingCampaign] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    nome: '',
    targetTags: [] as string[],
    targetEtiquetas: [] as string[], // Novo campo para etiquetas
    sessionNames: [] as string[],
    sessionName: '',
    messageType: 'sequence',
    messageContent: { sequence: [] as Array<{ type: string; content: any }> } as MessageContent,
    randomDelay: 30,
    startImmediately: true,
    scheduledFor: ''
  });

  // Vínculo Bot/Agente States
  const [vinculoTipo, setVinculoTipo] = useState<'AGENTE' | 'BOT' | null>(null);
  const [vinculoId, setVinculoId] = useState<string | null>(null);
  const [vinculoName, setVinculoName] = useState<string | null>(null);

  useEffect(() => {
    loadCampaigns();
    loadContactTags();
    loadWhatsAppSessions();
    loadEtiquetas();
    loadTotalSentDispatches();
  }, [selectedTenantId]);

  // Effect para busca em tempo real
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      // Quando a busca mudar, voltar para primeira página
      loadCampaigns(1, search);
    }, 300); // Debounce de 300ms

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  // Handle campaign creation based on permissions
  const handleOpenCampaignTypeSelection = () => {
    // 🚦 Verificar se atingiu 100% do limite
    if (dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100) {
      toast.error('Limite de disparos mensais atingido. Não é possível criar novas campanhas.');
      return;
    }

    const whatsWebAllowed = canUseWhatsWeb();
    const officialApiAllowed = canUseOfficialApi();

    // If neither is allowed, show error
    if (!whatsWebAllowed && !officialApiAllowed) {
      toast.error('Você não tem permissão para criar campanhas. Entre em contato com o administrador.');
      return;
    }

    // If only WhatsApp Web is allowed, go directly to it
    if (whatsWebAllowed && !officialApiAllowed) {
      setShowCreateModal(true);
      return;
    }

    // If only Official API is allowed, go directly to it
    if (!whatsWebAllowed && officialApiAllowed) {
      setShowOfficialApiModal(true);
      return;
    }

    // If both are allowed, show the selection modal
    setShowCampaignTypeModal(true);
  };

  // Funções de navegação de paginação
  const handlePreviousPage = () => {
    if (campaignsCurrentPage > 1) {
      loadCampaigns(campaignsCurrentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (campaignsCurrentPage < totalPages) {
      loadCampaigns(campaignsCurrentPage + 1);
    }
  };

  const handleGoToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      loadCampaigns(page);
    }
  };

  // Funções de busca
  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCampaignsCurrentPage(1); // Reset para primeira página ao buscar
  };

  const handleClearSearch = () => {
    setSearch('');
    setCampaignsCurrentPage(1);
  };

  // Auto-ajustar altura dos textareas de legenda
  useEffect(() => {
    const textareas = document.querySelectorAll('textarea[placeholder="Legenda..."]');
    textareas.forEach((textarea) => {
      const element = textarea as HTMLTextAreaElement;
      element.style.height = 'auto';
      element.style.height = element.scrollHeight + 'px';
    });
  }, [formData.messageContent, showCreateModal]);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const savedTenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (savedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = savedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadCampaigns = async (page: number = campaignsCurrentPage, searchTerm: string = search) => {
    try {
      setLoading(true);
      
      // Determinar qual tipo de campanha carregar baseado nas permissões
      const whatsWebAllowed = canUseWhatsWeb();
      const officialApiAllowed = canUseOfficialApi();
      
      // Construir query params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: campaignsPerPage.toString()
      });
      
      // Adicionar search se houver
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Adicionar filtro de tipo de campanha baseado nas permissões
      if (whatsWebAllowed && !officialApiAllowed) {
        // Apenas WhatsApp Web permitido
        params.append('campaignType', 'WHATS_WEB');
      } else if (!whatsWebAllowed && officialApiAllowed) {
        // Apenas API Oficial permitida
        params.append('campaignType', 'OFFICIAL_API');
      }
      // Se ambos permitidos, não envia filtro (mostra todos)
      // Se nenhum permitido, não envia filtro (backend retornará vazio pelo tenant)
      
      const response = await authenticatedFetch(`/api/campaigns?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      
      // 🔍 DEBUG: Log de campanhas carregadas (DESABILITADO em produção)
      // console.log('📊 CAMPANHAS CARREGADAS:', {
      //   total: data.campaigns?.length || 0,
      //   campanhas: data.campaigns?.map((c: Campaign) => ({
      //     id: c.id.substring(0, 8),
      //     nome: c.nome,
      //     status: c.status,
      //     tipo: c.campaignType
      //   }))
      // });
      
      setCampaigns(data.campaigns || []);
      setTotalCampaigns(data.total || 0);
      setTotalPages(data.totalPages || 0);
      setCampaignsCurrentPage(page);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const loadTotalSentDispatches = async () => {
    try {
      const response = await authenticatedFetch('/api/dispatch-statistics/total');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setTotalSentDispatches(data.data.totalSent);
        setDispatchLimit(data.data.limit); // null = ilimitado
        setDispatchPercentage(data.data.percentageUsed || 0);
      }
    } catch (error) {
      console.error('Erro ao carregar total de disparos:', error);
      // Silencioso - não mostra toast de erro
    }
  };

  const loadContactTags = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/tags');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const tags = await response.json();
      setContactTags(tags);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadEtiquetas = async () => {
    try {
      setLoadingEtiquetas(true);
      
      // Buscar dados do tenant
      const tenant = await apiService.getCurrentTenant();
      
      if (!tenant.plataforma || !tenant.idPlataforma) {
        console.warn('Tenant não possui plataforma ou ID da plataforma configurados');
        setEtiquetas([]);
        return;
      }

      // Buscar etiquetas da API externa
      const response = await apiService.getEtiquetas(tenant.plataforma, tenant.idPlataforma);
      setEtiquetas(response.etiquetas || []);
    } catch (error) {
      console.error('Erro ao carregar etiquetas:', error);
      setEtiquetas([]);
    } finally {
      setLoadingEtiquetas(false);
    }
  };

  const handleAddEtiqueta = (etiqueta: Etiqueta) => {
    if (!selectedEtiquetas.find(e => e.id === etiqueta.id)) {
      const newSelected = [...selectedEtiquetas, etiqueta];
      setSelectedEtiquetas(newSelected);
      // Atualizar formData com os títulos das etiquetas
      setFormData(prev => ({
        ...prev,
        targetEtiquetas: newSelected.map(e => e.title)
      }));
    }
  };

  const handleRemoveEtiqueta = (etiquetaId: number) => {
    const newSelected = selectedEtiquetas.filter(e => e.id !== etiquetaId);
    setSelectedEtiquetas(newSelected);
    // Atualizar formData com os títulos das etiquetas
    setFormData(prev => ({
      ...prev,
      targetEtiquetas: newSelected.map(e => e.title)
    }));
  };

  const loadWhatsAppSessions = async () => {
    try {
      const response = await authenticatedFetch('/api/campaigns/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const sessions = await response.json();
      setWhatsappSessions(sessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  const handleTagToggle = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tag)
        ? prev.targetTags.filter(t => t !== tag)
        : [...prev.targetTags, tag]
    }));
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter(s => s !== sessionName)
        : [...prev.sessionNames, sessionName]
    }));
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.targetTags.length === 0 && formData.targetEtiquetas.length === 0) {
      toast.error('Selecione pelo menos uma categoria ou etiqueta de contatos');
      return;
    }

    if (formData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma conexão WhatsApp');
      return;
    }

    if (!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) {
      toast.error('Adicione pelo menos uma mensagem à campanha');
      return;
    }

    try {
      const sequence = formData.messageContent.sequence;
      const isSequence = sequence.length > 1;

      let finalMessageType: string;
      let finalMessageContent: any;

      if (isSequence) {
        finalMessageType = 'sequence';
        finalMessageContent = { sequence };
      } else {
        const singleMessage = sequence[0];
        finalMessageType = singleMessage.type;
        finalMessageContent = singleMessage.content;
      }

      const campaignData = {
        ...formData,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        scheduledFor: formData.startImmediately 
          ? null 
          : formData.scheduledFor 
            ? (() => {
                // ✅ Converter para ISO string com timezone correto
                const localDate = new Date(formData.scheduledFor);
                return localDate.toISOString();
              })()
            : null,
        vinculoTipo: vinculoTipo, // ✅ Tipo de vínculo (AGENTE ou BOT)
        vinculoId: vinculoId, // ✅ ID do agente ou bot
        vinculoName: vinculoName // ✅ Nome do agente ou título do bot
      };

      const response = await authenticatedFetch('/api/campaigns', {
        method: 'POST',
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map((err: any) => err.msg).join(', ');
          throw new Error(validationErrors);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      toast.success('Campanha criada com sucesso!');
      setShowCreateModal(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar campanha');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      targetTags: [],
      targetEtiquetas: [],
      sessionNames: [],
      sessionName: '',
      messageType: 'sequence',
      messageContent: { sequence: [] },
      randomDelay: 30,
      startImmediately: true,
      scheduledFor: ''
    });
    setSelectedEtiquetas([]);
    setVinculoTipo(null);
    setVinculoId(null);
    setVinculoName(null);
    setUploadingFiles({});
    setFileInfos({});
  };

  // Handler para seleção de Bot/Agente
  const handleVinculoSelect = (tipo: 'AGENTE' | 'BOT' | null, id: string | null, name: string | null) => {
    setVinculoTipo(tipo);
    setVinculoId(id);
    setVinculoName(name);
  };

  const handleFileUpload = async (file: File, messageIndex: number, variationIndex?: number) => {
    const uploadKey = variationIndex !== undefined ? `${messageIndex}-${variationIndex}` : messageIndex.toString();
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const savedTenantId = localStorage.getItem('selected_tenant_id');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (savedTenantId) {
        (headers as Record<string, string>)['X-Tenant-Id'] = savedTenantId;
      }

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      // Armazenar informações do arquivo
      if (variationIndex !== undefined) {
        setFileInfos(prev => ({
          ...prev,
          [`${messageIndex}-${variationIndex}`]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      } else {
        setFileInfos(prev => ({
          ...prev,
          [messageIndex]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      }

      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];

      if (variationIndex !== undefined) {
        // Upload para variação
        const newSequence = currentSequence.map((seqItem, i) => {
          if (i === messageIndex) {
            const mediaVariations = [...(seqItem.content.mediaVariations || [])];
            // Garantir que o array tenha o tamanho necessário
            while (mediaVariations.length <= variationIndex) {
              mediaVariations.push({ url: '', caption: '', fileName: '' });
            }
            mediaVariations[variationIndex] = {
              ...mediaVariations[variationIndex],
              url: data.fileUrl,
              fileName: data.originalName
            };
            return {
              ...seqItem,
              content: { ...seqItem.content, mediaVariations }
            };
          }
          return seqItem;
        });

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      } else {
        // Upload para modo single
        const newSequence = currentSequence.map((seqItem, i) =>
          i === messageIndex ? {
            ...seqItem,
            content: { ...seqItem.content, url: data.fileUrl }
          } : seqItem
        );

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      }

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleRemoveFile = (messageIndex: number) => {
    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = currentSequence.map((seqItem, i) =>
      i === messageIndex ? {
        ...seqItem,
        content: { ...seqItem.content, url: '' }
      } : seqItem
    );

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));

    setFileInfos(prev => {
      const updated = { ...prev };
      delete updated[messageIndex];
      return updated;
    });

    toast.success('Arquivo removido');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    return '📄';
  };

  const handleToggleCampaign = (campaignId: string, action: 'pause' | 'resume') => {
    setCampaignToToggle({ id: campaignId, action });
    
    if (action === 'pause') {
      setShowPauseConfirm(true);
    } else {
      setShowResumeConfirm(true);
    }
  };

  const confirmToggleCampaign = async () => {
    if (!campaignToToggle) return;

    const { id, action } = campaignToToggle;

    try {
      setTogglingCampaign(id);
      
      const response = await authenticatedFetch(`/api/campaigns/${id}/${action}`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      toast.success(result.message || `Campanha ${action === 'pause' ? 'pausada' : 'retomada'} com sucesso!`);
      loadCampaigns();
      
      setShowPauseConfirm(false);
      setShowResumeConfirm(false);
      setCampaignToToggle(null);
    } catch (error: any) {
      console.error('Erro ao alterar status da campanha:', error);
      toast.error(error.message || 'Erro ao alterar status da campanha');
    } finally {
      setTogglingCampaign(null);
    }
  };

  const cancelToggleCampaign = () => {
    setShowPauseConfirm(false);
    setShowResumeConfirm(false);
    setCampaignToToggle(null);
  };

  const handleDeleteCampaign = (campaignId: string) => {
    setCampaignToDelete(campaignId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    try {
      const response = await authenticatedFetch(`/api/campaigns/${campaignToDelete}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success('Campanha excluída com sucesso!');
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao excluir campanha:', error);
      toast.error('Erro ao excluir campanha');
    } finally {
      setShowDeleteConfirm(false);
      setCampaignToDelete(null);
    }
  };

  const cancelDeleteCampaign = () => {
    setShowDeleteConfirm(false);
    setCampaignToDelete(null);
  };

  const handleViewReport = async (campaignId: string) => {
    // Encontrar a campanha para verificar o tipo
    const campaign = campaigns.find(c => c.id === campaignId);
    
    if (!campaign) {
      toast.error('Campanha não encontrada');
      return;
    }

    const campaignType = campaign.campaignType || 'WHATS_WEB';
    
    setCurrentReportCampaignId(campaignId);
    setCurrentReportCampaignType(campaignType);
    setReportCurrentPage(1);
    setShowReportModal(true);

    // Se for API Oficial, não precisa carregar dados aqui (o componente faz isso)
    if (campaignType === 'OFFICIAL_API') {
      return;
    }

    // Se for WhatsApp Web, carregar dados como antes
    setReportLoading(true);

    try {
      const response = await authenticatedFetch(`/api/campaigns/${campaignId}/report`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
        setShowReportModal(false);
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
      setShowReportModal(false);
    } finally {
      setReportLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gray-100 text-gray-800';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Aguardando';
      case 'RUNNING':
        return 'Executando';
      case 'COMPLETED':
        return 'Concluída';
      case 'PAUSED':
        return 'Pausada';
      case 'FAILED':
        return 'Falha';
      default:
        return status;
    }
  };

  // Função para formatar data de envio/agendamento
  const getDateDisplay = (campaign: Campaign) => {
    // Se tem data agendada e ainda não começou
    if (campaign.scheduledFor && !campaign.startedAt) {
      const date = new Date(campaign.scheduledFor);
      return {
        label: 'Agendada',
        date: date.toLocaleString('pt-BR', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    }
    
    // Se já começou a ser enviada
    if (campaign.startedAt) {
      const date = new Date(campaign.startedAt);
      return {
        label: 'Iniciada',
        date: date.toLocaleString('pt-BR', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    }

    return null;
  };

  // 🚦 Função para obter configurações visuais do alerta de limite
  const getDispatchLimitAlertStyle = () => {
    if (!dispatchLimit || dispatchLimit === 0) {
      // Ilimitado - estilo normal
      return {
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--border-color)',
        borderWidth: '1px',
        textColor: 'var(--text-primary)',
        showWarning: false,
        warningText: '',
        warningColor: ''
      };
    }

    // 100% ou mais - vermelho forte
    if (dispatchPercentage >= 100) {
      return {
        backgroundColor: '#FEE2E2', // red-100
        borderColor: '#DC2626', // red-600
        borderWidth: '2px',
        textColor: '#991B1B', // red-800
        showWarning: true,
        warningText: 'Limite de envios 100%',
        warningColor: '#DC2626'
      };
    }

    // 95% ou mais - laranja forte
    if (dispatchPercentage >= 95) {
      return {
        backgroundColor: '#FED7AA', // orange-200
        borderColor: '#EA580C', // orange-600
        borderWidth: '2px',
        textColor: '#9A3412', // orange-800
        showWarning: true,
        warningText: '95% do limite atingido',
        warningColor: '#EA580C'
      };
    }

    // 85% ou mais - amarelo
    if (dispatchPercentage >= 85) {
      return {
        backgroundColor: '#FEF3C7', // yellow-100
        borderColor: '#D97706', // yellow-600
        borderWidth: '2px',
        textColor: '#92400E', // yellow-800
        showWarning: true,
        warningText: '85% do limite atingido',
        warningColor: '#D97706'
      };
    }

    // Abaixo de 85% - estilo normal
    return {
      backgroundColor: 'var(--bg-secondary)',
      borderColor: 'var(--border-color)',
      borderWidth: '1px',
      textColor: 'var(--text-primary)',
      showWarning: false,
      warningText: '',
      warningColor: ''
    };
  };

  const handleRefreshReport = async () => {
    if (!currentReportCampaignId) return;

    setReportLoading(true);
    try {
      const response = await authenticatedFetch(`/api/campaigns/${currentReportCampaignId}/report`);

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!currentReportCampaignId || !reportData) return;

    try {
      const response = await authenticatedFetch(`/api/campaigns/${currentReportCampaignId}/report/download`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `relatorio-campanha-${reportData.campaign.nome}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Relatório baixado com sucesso!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao baixar relatório');
      }
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error('Erro ao baixar relatório');
    }
  };

  // Create actions JSX for campaigns page
  const campaignsActions = (
    <div className="flex gap-2">
      {/* Botão Atualizar */}
      <button
        onClick={() => {
          console.log('🔄 Atualizando lista de campanhas manualmente...');
          loadCampaigns();
        }}
        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all flex items-center gap-2 text-sm font-medium"
        title="Atualizar lista de campanhas"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Atualizar
      </button>
      
      {/* Botão Nova Campanha */}
      <button
        onClick={handleOpenCampaignTypeSelection}
        className="btn-primary"
        disabled={dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100}
        style={{
          opacity: (dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100) ? 0.5 : 1,
          cursor: (dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100) ? 'not-allowed' : 'pointer'
        }}
        title={(dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100) ? 'Limite de disparos atingido' : ''}
      >
        + Nova Campanha
      </button>
    </div>
  );

  // Register actions and metadata for iframe mode
  useEffect(() => {
    if (isUrlLogin) {
      registerActions('/campanhas', campaignsActions);
      registerMetadata('/campanhas', {
        title: 'Campanhas',
        subtitle: `${campaigns.length} ${campaigns.length === 1 ? 'campanha' : 'campanhas'}`
      });
    }
    return () => clearActions('/campanhas');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrlLogin, dispatchLimit, dispatchPercentage, loading, campaigns.length]);

  const getErrorIcon = (errorMessage: string) => {
    if (!errorMessage || errorMessage === 'N/A') return null;

    const error = errorMessage.toLowerCase();

    if (error.includes('network') || error.includes('connection') || error.includes('timeout')) {
      return {
        icon: '🌐',
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
        category: 'Conectividade'
      };
    }

    if (error.includes('blocked') || error.includes('banned') || error.includes('spam')) {
      return {
        icon: '🚫',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        category: 'Bloqueio'
      };
    }

    if (error.includes('rate limit') || error.includes('quota') || error.includes('limit')) {
      return {
        icon: '⏰',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        category: 'Rate Limit'
      };
    }

    if (error.includes('invalid') || error.includes('not found') || error.includes('number')) {
      return {
        icon: '📱',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        category: 'Número Inválido'
      };
    }

    if (error.includes('permission') || error.includes('unauthorized') || error.includes('forbidden')) {
      return {
        icon: '🔒',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        category: 'Permissão'
      };
    }

    if (error.includes('session') || error.includes('disconnected') || error.includes('logout')) {
      return {
        icon: '📲',
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100',
        category: 'Sessão'
      };
    }

    if (error.includes('media') || error.includes('file') || error.includes('download')) {
      return {
        icon: '📁',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        category: 'Mídia'
      };
    }

    // Erro genérico
    return {
      icon: '⚠️',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      category: 'Erro Geral'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Carregando campanhas...</span>
      </div>
    );
  }

  return (
    <>
      {/* Header - Only show in normal mode */}
      {!isUrlLogin && (
      <header className="border-b px-6 py-4 transition-colors" style={{ 
        backgroundColor: 'var(--bg-card)',
        borderColor: 'var(--border-color)'
      }}>
        <div className="flex items-center justify-between">
          {/* Título e Subtítulo */}
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Campanhas</h1>
              <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                {search ? `${totalCampaigns} ${totalCampaigns === 1 ? 'campanha encontrada' : 'campanhas encontradas'}` : `${totalCampaigns} ${totalCampaigns === 1 ? 'campanha ativa' : 'campanhas ativas'}`}
              </p>
            </div>
          </div>

          {/* Ações da direita */}
          <div className="flex items-center space-x-4">
            {/* Contador de Disparos com Alertas */}
            {totalSentDispatches !== null && (() => {
              const alertStyle = getDispatchLimitAlertStyle();
              
              return (
                <div className="hidden md:flex flex-col items-center px-3 py-1.5 rounded-lg transition-all duration-300" style={{
                  backgroundColor: alertStyle.backgroundColor,
                  border: `${alertStyle.borderWidth} solid ${alertStyle.borderColor}`
                }}>
                  {/* Linha principal */}
                  <div className="flex items-center">
                    {dispatchPercentage < 100 ? (
                      <>
                        <span className="text-sm font-normal" style={{ color: alertStyle.textColor }}>
                          Total enviados:
                        </span>
                        <span className="ml-2 text-sm font-semibold" style={{ color: alertStyle.textColor }}>
                          {totalSentDispatches.toLocaleString('pt-BR')}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm font-semibold" style={{ color: alertStyle.warningColor }}>
                        {alertStyle.warningText}
                      </span>
                    )}
                  </div>
                  
                  {/* Linha de aviso (85% e 95%) */}
                  {alertStyle.showWarning && dispatchPercentage < 100 && (
                    <div className="mt-0.5 text-xs font-normal text-center" style={{ color: alertStyle.warningColor }}>
                      {alertStyle.warningText}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Botões de Ação */}
            <div className="flex gap-2">
              {/* Botão Atualizar */}
              <button
                onClick={() => {
                  console.log('🔄 Atualizando lista de campanhas manualmente...');
                  loadCampaigns();
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all flex items-center gap-2 text-sm font-medium"
                title="Atualizar lista de campanhas"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Atualizar
              </button>
              
              {/* Botão Nova Campanha */}
              <button
                onClick={handleOpenCampaignTypeSelection}
                className="btn-primary"
                disabled={dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100}
                style={{
                  opacity: (dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100) ? 0.5 : 1,
                  cursor: (dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100) ? 'not-allowed' : 'pointer'
                }}
                title={(dispatchLimit && dispatchLimit > 0 && dispatchPercentage >= 100) ? 'Limite de disparos atingido' : ''}
              >
                + Nova Campanha
              </button>
            </div>
          </div>
        </div>
      </header>
      )}

      <div className="p-6 space-y-6">
        {/* Barra de Busca */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="campaign-search" className="sr-only">
              Buscar campanhas
            </label>
            <input
              id="campaign-search"
              type="text"
              placeholder="Buscar campanhas por nome..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors text-sm font-normal"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)'
              }}
              aria-label="Campo de busca para campanhas"
            />
          </div>

          {search && (
            <button
              onClick={handleClearSearch}
              className="px-4 py-2 hover:opacity-80 focus:outline-none focus:underline transition-opacity text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
              aria-label="Limpar busca"
            >
              Limpar
            </button>
          )}
        </div>

        <div className="rounded-xl shadow-sm border transition-colors" style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-color)'
        }}>
          <div className="px-6 py-4 border-b transition-colors" style={{ borderColor: 'var(--border-color)' }}>
            <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Campanhas Criadas</h3>
          </div>

          {campaigns.length === 0 ? (
            <div className="p-6 text-center">
              {search ? (
                <>
                  <p className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>Nenhuma campanha encontrada para "{search}"</p>
                  <button 
                    onClick={handleClearSearch}
                    className="mt-2 text-sm font-medium text-blue-600 hover:underline"
                  >
                    Limpar busca
                  </button>
                </>
              ) : (
                <p className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>Nenhuma campanha encontrada. Crie sua primeira campanha para começar.</p>
              )}
            </div>
          ) : (
            <>
              {/* Cabeçalho da Tabela */}
              <div
                className="px-6 py-3 transition-colors"
                style={{
                  borderTop: theme === 'dark' ? '1px solid #3F3F46' : '1px solid var(--border-color)',
                  borderBottom: theme === 'dark' ? '1px solid #3F3F46' : '1px solid var(--border-color)',
                  borderLeft: 'none',
                  borderRight: 'none',
                  backgroundColor: theme === 'dark' ? '#1F1F23' : '#F9FAFB',
                  color: 'var(--text-secondary)',
                  boxShadow: theme === 'dark' 
                    ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
                    : '0 2px 8px rgba(15, 23, 42, 0.08)'
                }}
              >
                <div className="flex items-center w-full">
                  {/* Nome Campanha - Centralizado */}
                  <div className="flex-1 min-w-0 pr-4" style={{ maxWidth: '200px' }}>
                    <h3 className="text-sm font-semibold text-left" style={{ color: 'var(--text-secondary)' }}>
                      Nome Campanha
                    </h3>
                  </div>

                  {/* Tipo Canal - Centralizado */}
                  <div className="w-24 flex justify-center">
                    <h3 className="text-sm font-semibold text-center" style={{ color: 'var(--text-secondary)' }}>
                      Tipo Canal
                    </h3>
                  </div>

                  {/* Status Campanha - Centralizado */}
                  <div className="w-32 flex justify-center">
                    <h3 className="text-sm font-semibold text-center" style={{ color: 'var(--text-secondary)' }}>
                      Status Campanha
                    </h3>
                  </div>

                  {/* Data de Envio - Centralizado */}
                  <div className="w-40 flex justify-center">
                    <h3 className="text-sm font-semibold text-center" style={{ color: 'var(--text-secondary)' }}>
                      Data de Envio
                    </h3>
                  </div>

                  {/* Espaçador para outras informações */}
                  <div className="flex-1"></div>
                </div>
              </div>

              {/* Container com scroll para campanhas */}
              <div className="max-h-[700px] overflow-y-auto divide-y divide-[var(--border-color-soft)] transition-colors" style={{ 
                borderColor: theme === 'dark' ? '#2A2A2A' : 'var(--border-color)' 
              }}>
                {campaigns.map((campaign) => (
                <div key={campaign.id} className="px-6 py-3 h-[60px] flex items-center">
                  <div className="flex items-center w-full">
                    {/* Nome Campanha - Alinhado à esquerda */}
                    <div className="flex-1 min-w-0 pr-4" style={{ maxWidth: '200px' }}>
                      <h4 
                        className="text-sm font-normal truncate text-left cursor-pointer" 
                        style={{ color: 'var(--text-primary)' }}
                        title={campaign.nome}
                      >
                        {campaign.nome}
                      </h4>
                    </div>

                    {/* Tipo Canal - Centralizado */}
                    <div className="w-24 flex justify-center">
                      <span 
                        className="inline-flex items-center justify-center px-2 py-1 rounded text-xs font-normal text-white"
                        style={{ 
                          backgroundColor: campaign.campaignType === 'OFFICIAL_API' ? '#0088CC' : '#58D58D',
                          minWidth: '60px'
                        }}
                      >
                        {campaign.campaignType === 'OFFICIAL_API' ? 'Oficial' : 'Web'}
                      </span>
                    </div>

                    {/* Status Campanha - Centralizado */}
                    <div className="w-32 flex justify-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-normal ${getStatusColor(campaign.status)}`}>
                        {getStatusText(campaign.status)}
                      </span>
                    </div>

                    {/* Data de Envio - Centralizado */}
                    <div className="w-40 flex flex-col items-center justify-center">
                      {getDateDisplay(campaign) ? (
                        <>
                          <span className="text-xs font-normal" style={{ color: 'var(--text-primary)' }}>
                            {getDateDisplay(campaign)?.date.split(',')[0]}
                          </span>
                          <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>
                            {getDateDisplay(campaign)?.date.split(',')[1]}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>-</span>
                      )}
                    </div>

                    {/* Informações essenciais */}
                    <div className="flex items-center gap-6 text-sm flex-1 justify-end pr-4 font-normal" style={{ color: 'var(--text-secondary)' }}>
                      <div className="flex items-center gap-1">
                        <span className="font-semibold">Enviados:</span>
                        <span>{campaign.sentCount}/{campaign.totalContacts}</span>
                      </div>

                      {/* Sessões ativas (apenas ícones) */}
                    </div>

                    {/* Botões de ação */}
                    <div className="flex gap-1">
                      {/* Botão de visualização - padrão com ícone de olho */}
                      <button
                        onClick={() => handleViewReport(campaign.id)}
                        className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                        title="Ver relatório da campanha"
                        aria-label={`Ver relatório da campanha ${campaign.nome}`}
                        style={{
                          background: (theme === 'dark' ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)'),
                          color: (theme === 'dark' ? '#EDEEF0' : '#1C2024')
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = (theme === 'dark' ? 'rgba(105, 110, 119, 0.2)' : 'rgba(139, 141, 152, 0.2)');
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = (theme === 'dark' ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)');
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      {/* Botão de ativar/retomar campanha - ícone Lucide play */}
                      {campaign.status === 'PAUSED' && (
                        <button
                          onClick={() => handleToggleCampaign(campaign.id, 'resume')}
                          className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                          title="Retomar campanha"
                          aria-label={`Retomar campanha ${campaign.nome}`}
                          style={{
                            background: 'rgba(16, 185, 129, 0.15)',
                            color: (theme === 'dark' ? '#86EFAC' : '#16A34A')
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)';
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                        </button>
                      )}
                      {/* Botão de excluir - padrão com ícone de lixeira */}
                      {['PENDING', 'COMPLETED', 'FAILED'].includes(campaign.status) && (
                        <button
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                          title="Excluir campanha"
                          aria-label={`Excluir campanha ${campaign.nome}`}
                          style={{
                            background: 'rgba(229, 70, 102, 0.15)',
                            color: (theme === 'dark' ? '#FF949D' : '#CA244D')
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(229, 70, 102, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)';
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                            <path d="M3 6h18" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>

              {/* Componente de Paginação */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t transition-colors flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
                  {/* Info de registros */}
                  <div className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                    Mostrando <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{campaigns.length}</span> de{' '}
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{totalCampaigns}</span> campanhas
                  </div>

                  {/* Controles de paginação */}
                  <div className="flex items-center gap-2">
                    {/* Botão Anterior */}
                    <button
                      onClick={handlePreviousPage}
                      disabled={campaignsCurrentPage === 1}
                      className="px-3 py-1.5 text-sm font-normal rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: campaignsCurrentPage === 1 ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                        color: campaignsCurrentPage === 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      ← Anterior
                    </button>

                    {/* Números de página */}
                    <div className="flex items-center gap-1">
                      {(() => {
                        const pages = [];
                        const maxVisible = 5;
                        let startPage = Math.max(1, campaignsCurrentPage - Math.floor(maxVisible / 2));
                        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                        
                        if (endPage - startPage < maxVisible - 1) {
                          startPage = Math.max(1, endPage - maxVisible + 1);
                        }

                        // Primeira página
                        if (startPage > 1) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => handleGoToPage(1)}
                              className="px-3 py-1.5 text-sm font-normal rounded-lg transition-colors"
                              style={{
                                backgroundColor: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border-color)'
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 2) {
                            pages.push(<span key="ellipsis1" className="px-2 text-sm font-normal" style={{ color: 'var(--text-muted)' }}>...</span>);
                          }
                        }

                        // Páginas visíveis
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handleGoToPage(i)}
                              className="px-3 py-1.5 text-sm font-normal rounded-lg transition-colors"
                              style={{
                                backgroundColor: i === campaignsCurrentPage ? '#3b82f6' : 'var(--bg-primary)',
                                color: i === campaignsCurrentPage ? '#ffffff' : 'var(--text-primary)',
                                border: `1px solid ${i === campaignsCurrentPage ? '#3b82f6' : 'var(--border-color)'}`
                              }}
                            >
                              {i}
                            </button>
                          );
                        }

                        // Última página
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(<span key="ellipsis2" className="px-2 text-sm font-normal" style={{ color: 'var(--text-muted)' }}>...</span>);
                          }
                          pages.push(
                            <button
                              key={totalPages}
                              onClick={() => handleGoToPage(totalPages)}
                              className="px-3 py-1.5 text-sm font-normal rounded-lg transition-colors"
                              style={{
                                backgroundColor: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border-color)'
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                    </div>

                    {/* Botão Próxima */}
                    <button
                      onClick={handleNextPage}
                      disabled={campaignsCurrentPage === totalPages}
                      className="px-3 py-1.5 text-sm font-normal rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: campaignsCurrentPage === totalPages ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                        color: campaignsCurrentPage === totalPages ? 'var(--text-muted)' : 'var(--text-primary)',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      Próxima →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal de Seleção de Tipo de Campanha */}
        <CampaignTypeModal
          isOpen={showCampaignTypeModal}
          onClose={() => setShowCampaignTypeModal(false)}
          onSelectWhatsWeb={() => {
            setShowCampaignTypeModal(false);
            setShowCreateModal(true);
          }}
          onSelectOfficialApi={() => {
            setShowCampaignTypeModal(false);
            setShowOfficialApiModal(true);
          }}
          canUseWhatsWeb={canUseWhatsWeb()}
          canUseOfficialApi={canUseOfficialApi()}
        />

        {/* Modal de Criação - API Oficial WhatsApp */}
        <OfficialApiCampaignModal
          isOpen={showOfficialApiModal}
          onClose={() => setShowOfficialApiModal(false)}
          onSuccess={() => {
            setShowOfficialApiModal(false);
            loadCampaigns();
          }}
        />

        {/* Modal de Criação - WhatsApp Web */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto transition-colors" style={{
              backgroundColor: 'var(--bg-card)'
            }}>
              <div className={`flex justify-between items-center p-6 border-b transition-colors ${theme === 'dark' ? '' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`} style={{
                borderColor: theme === 'dark' ? '#717070' : 'var(--border-color)',
                background: theme === 'dark' ? '#404044' : undefined
              }}>
                <div>
                  <h3 className="text-xl font-semibold text-n-slate-12 flex items-center gap-2">
                    <span>Nova Campanha</span>
                    <TooltipHelp id="campaigns_web_create" />
                  </h3>
                  <p className="text-sm mt-1 text-n-slate-11">Configure sua campanha de mensagens WhatsApp</p>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="hover:bg-opacity-80 p-2 rounded-full transition-colors"
                  style={{ color: theme === 'dark' ? '#ffffff' : 'var(--text-muted)' }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateCampaign} className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                  {/* COLUNA ESQUERDA - Informações Básicas */}
                  <div className="space-y-6">
                    <div className={`rounded-lg p-4 border transition-colors ${theme === 'light' ? 'bg-blue-50 border-blue-200' : ''}`} style={{
                      background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : undefined,
                      borderColor: theme === 'dark' ? 'var(--border-color)' : undefined
                    }}>
                      <h4 className={`text-lg font-semibold mb-2 ${theme === 'light' ? 'text-blue-900' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>Informações Básicas</h4>
                      <p className={`text-sm ${theme === 'light' ? 'text-blue-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>Configure os dados fundamentais da sua campanha</p>
                    </div>

                    {/* Nome da Campanha */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        Nome da Campanha *
                      </label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        style={{
                          backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                          borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                          color: theme === 'dark' ? 'white' : '#1f2937'
                        }}
                        placeholder="Ex: Promoção Black Friday 2024"
                        required
                      />
                    </div>

                    {/* Bot e Agente de IA — DESATIVADO TEMPORARIAMENTE (descomentar quando necessário)
                    <div>
                      <BotAgenteSelector
                        selectedVinculoTipo={vinculoTipo}
                        selectedVinculoId={vinculoId}
                        selectedVinculoName={vinculoName}
                        onSelect={handleVinculoSelect}
                      />
                    </div>
                    */}

                    {/* Categorias de Contatos */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        Categorias de Contatos
                      </label>
                      <p className={`text-xs mb-3 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>Selecione quais categorias de contatos receberão a campanha</p>
                      <div className="max-h-40 overflow-y-auto border rounded-md p-3 transition-colors" style={{
                        backgroundColor: theme === 'dark' ? '#2A2A2A' : '#f9fafb',
                        borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db'
                      }}>
                        {contactTags.map((tag) => (
                          <label key={tag.id} className="flex items-center space-x-2 mb-2 p-2 rounded cursor-pointer transition-colors" style={{
                            '--hover-bg': theme === 'dark' ? '#3F3F46' : 'white'
                          } as React.CSSProperties}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3F3F46' : 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.targetTags.includes(tag.id)}
                              onChange={() => handleTagToggle(tag.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium" style={{ color: theme === 'dark' ? 'var(--text-primary)' : '#374151' }}>{tag.nome}</span>
                          </label>
                        ))}
                        {/* MODIFICADO: Mensagem vazia adaptada para tema dark */}
                        {/* ANTES: text-gray-500 (cor fixa) */}
                        {contactTags.length === 0 && (
                          <p className="text-sm text-center py-4" style={{ color: 'var(--text-secondary)' }}>Nenhuma categoria disponível</p>
                        )}
                      </div>
                      {formData.targetTags.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.targetTags.length} categoria(s) selecionada(s)</p>
                      )}
                    </div>

                    {/* Etiquetas da Plataforma */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        Etiquetas da Plataforma
                      </label>
                      <p className={`text-xs mb-3 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>Selecione etiquetas da sua plataforma para filtrar contatos</p>
                      
                      {/* MODIFICADO: Container de etiquetas selecionadas adaptado para tema dark */}
                      {/* ANTES: border-gray-300, bg-gray-50 (cores fixas) */}
                      {selectedEtiquetas.length > 0 && (
                        <div
                          className="flex flex-wrap gap-2 mb-3 p-3 rounded-lg border transition-colors"
                          style={{
                            borderColor: 'var(--border-color)',
                            backgroundColor: theme === 'dark' ? '#1a1a1b' : '#f9fafb'
                          }}
                        >
                          {selectedEtiquetas.map((etiqueta) => (
                            <div
                              key={etiqueta.id}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                              style={{
                                backgroundColor: theme === 'dark' ? 'rgba(74, 158, 255, 0.2)' : '#e0f2fe',
                                color: theme === 'dark' ? '#4a9eff' : '#0369a1',
                                border: `1px solid ${theme === 'dark' ? 'rgba(74, 158, 255, 0.3)' : '#7dd3fc'}`
                              }}
                            >
                              <span>{etiqueta.title}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveEtiqueta(etiqueta.id)}
                                className="hover:opacity-70 transition-opacity"
                                aria-label={`Remover etiqueta ${etiqueta.title}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* MODIFICADO: Dropdown para adicionar etiquetas adaptado para tema dark */}
                      {/* ANTES: border-gray-300, bg-gray-50, text-gray-500 (cores fixas) */}
                      {loadingEtiquetas ? (
                        <div
                          className="border rounded-md p-3 flex items-center justify-center transition-colors"
                          style={{
                            borderColor: 'var(--border-color)',
                            backgroundColor: theme === 'dark' ? '#1a1a1b' : '#f9fafb',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24" style={{ color: 'var(--text-secondary)' }}>
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Carregando etiquetas...</span>
                        </div>
                      ) : etiquetas.length === 0 ? (
                        <div
                          className="border rounded-md p-3 text-center transition-colors"
                          style={{
                            borderColor: 'var(--border-color)',
                            backgroundColor: theme === 'dark' ? '#1a1a1b' : '#f9fafb',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          Nenhuma etiqueta disponível. Configure a plataforma no cadastro da empresa.
                        </div>
                      ) : (
                        <select
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                          style={{
                            backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                            borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                            color: theme === 'dark' ? 'white' : '#1f2937'
                          }}
                          onChange={(e) => {
                            const etiqueta = etiquetas.find(etiq => etiq.id.toString() === e.target.value);
                            if (etiqueta) {
                              handleAddEtiqueta(etiqueta);
                              e.target.value = ''; // Reset select
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="">Selecione uma etiqueta para adicionar</option>
                          {etiquetas
                            .filter(etiq => !selectedEtiquetas.find(selected => selected.id === etiq.id))
                            .map((etiqueta) => (
                              <option key={etiqueta.id} value={etiqueta.id}>
                                {etiqueta.title}
                              </option>
                            ))
                          }
                        </select>
                      )}
                      
                      {selectedEtiquetas.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {selectedEtiquetas.length} etiqueta(s) selecionada(s)</p>
                      )}
                    </div>

                    {/* Conexões WhatsApp */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        Conexões WhatsApp * ({formData.sessionNames.length} selecionadas)
                      </label>
                      <div className={`mb-3 p-3 rounded-lg border transition-colors ${theme === 'light' ? 'bg-green-50 border-green-200' : ''}`} style={{
                        background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : undefined,
                        borderColor: theme === 'dark' ? 'var(--border-color)' : undefined
                      }}>
                        <div className="flex items-start">
                          <svg className={`h-5 w-5 mt-0.5 mr-2 flex-shrink-0 ${theme === 'light' ? 'text-green-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--success)' : undefined }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className={`text-sm font-medium ${theme === 'light' ? 'text-green-800' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>Multi-Sessão com Failover</h4>
                            <p className={`text-sm mt-1 ${theme === 'light' ? 'text-green-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                              Sistema inteligente que distribui envios entre múltiplas conexões com redundância automática.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="max-h-40 overflow-y-auto border rounded-md p-3 transition-colors" style={{
                        backgroundColor: theme === 'dark' ? '#2A2A2A' : '#f9fafb',
                        borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db'
                      }}>
                        {whatsappSessions.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma conexão WhatsApp disponível</p>
                        ) : (
                          whatsappSessions.map((session) => (
                            <label key={session.name} className="flex items-center justify-between space-x-2 mb-2 p-2 rounded cursor-pointer transition-colors" style={{
                              '--hover-bg': theme === 'dark' ? '#3F3F46' : 'white'
                            } as React.CSSProperties}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3F3F46' : 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.sessionNames.includes(session.name)}
                                  onChange={() => handleSessionToggle(session.name)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                  <span className="text-sm font-medium" style={{ color: theme === 'dark' ? 'var(--text-primary)' : '#374151' }}>
                                    {session.mePushName || session.displayName || session.name}
                                  </span>
                                  <span className="text-xs block" style={{ color: theme === 'dark' ? 'var(--text-secondary)' : '#6b7280' }}>({session.displayName || session.name})</span>
                                </div>
                              </div>
                                <span
                                  className="text-xs px-2 py-1 rounded-full"
                                  style={{
                                    backgroundColor: theme === 'dark' ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7',
                                    color: theme === 'dark' ? '#4ade80' : '#166534'
                                  }}
                                >
                                  Ativa
                                </span>
                            </label>
                          ))
                        )}
                      </div>
                      {formData.sessionNames.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.sessionNames.length} conexão(ões) selecionada(s)</p>
                      )}
                    </div>

                    {/* Configuração de Envio */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        Tempo de Randomização (segundos)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="300"
                        value={formData.randomDelay}
                        onChange={(e) => setFormData(prev => ({ ...prev, randomDelay: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                        style={{
                          backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                          borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                          color: theme === 'dark' ? 'white' : '#1f2937'
                        }}
                      />
                      <p className={`text-xs mt-1 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                        ⏱️ Intervalo aleatório: {formData.randomDelay >= 5 ? `5-${formData.randomDelay}s` : '5s (mínimo)'} • Recomendado: 30-60s
                      </p>
                    </div>

                    {/* Data de Envio */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                        Quando Enviar
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors" style={{
                          backgroundColor: theme === 'dark' ? '#2A2A2A' : '#ffffff',
                          borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)'
                        }}>
                          <input
                            type="radio"
                            checked={formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: true }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium" style={{ color: theme === 'dark' ? 'var(--text-primary)' : '#374151' }}>🚀 Iniciar imediatamente</span>
                            <p className="text-xs" style={{ color: theme === 'dark' ? 'var(--text-secondary)' : '#6b7280' }}>A campanha será executada assim que for criada</p>
                          </div>
                        </label>
                        <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors" style={{
                          backgroundColor: theme === 'dark' ? '#2A2A2A' : '#ffffff',
                          borderColor: theme === 'dark' ? '#3F3F46' : 'var(--border-color)'
                        }}>
                          <input
                            type="radio"
                            checked={!formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: false }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium" style={{ color: theme === 'dark' ? 'var(--text-primary)' : '#374151' }}>📅 Agendar para:</span>
                            <p className="text-xs" style={{ color: theme === 'dark' ? 'var(--text-secondary)' : '#6b7280' }}>Escolha data e hora específicas</p>
                          </div>
                        </label>
                        {!formData.startImmediately && (
                          <input
                            type="datetime-local"
                            value={formData.scheduledFor}
                            onChange={(e) => setFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ml-6 transition-colors"
                            style={{
                              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                              borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                              color: theme === 'dark' ? 'white' : '#1f2937'
                            }}
                            required={!formData.startImmediately}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* COLUNA DIREITA - Mensagens */}
                  <div className="space-y-6">
                    <div className={`rounded-lg p-4 border transition-colors ${theme === 'light' ? 'bg-purple-50 border-purple-200' : ''}`} style={{
                      background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : undefined,
                      borderColor: theme === 'dark' ? 'var(--border-color)' : undefined
                    }}>
                      <h4 className={`text-lg font-semibold mb-2 ${theme === 'light' ? 'text-purple-900' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>💬 Mensagens da Campanha</h4>
                      <p className={`text-sm ${theme === 'light' ? 'text-purple-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>Configure o conteúdo que será enviado aos contatos</p>
                    </div>

                    <div>
                      <div className={`mb-4 p-3 rounded-lg border transition-colors ${theme === 'light' ? 'bg-blue-50 border-blue-200' : ''}`} style={{
                        background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : undefined,
                        borderColor: theme === 'dark' ? 'var(--border-color)' : undefined
                      }}>
                        <div className="flex items-start">
                          <svg className={`h-5 w-5 mt-0.5 mr-2 ${theme === 'light' ? 'text-blue-400' : ''}`} style={{ color: theme === 'dark' ? 'var(--warning)' : undefined }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className={`text-sm font-medium ${theme === 'light' ? 'text-blue-800' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>💡 Variáveis Dinâmicas</h4>
                            <p className={`text-sm mt-1 ${theme === 'light' ? 'text-blue-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>
                              Personalize suas mensagens com:
                              <span className="block mt-1">
                                <code className={theme === 'light' ? 'bg-blue-100 px-1 rounded mx-1' : 'bg-blue-600 text-white px-1 rounded mx-1'}>{'{{nome}}'}</code>
                                <code className={theme === 'light' ? 'bg-blue-100 px-1 rounded mx-1' : 'bg-blue-600 text-white px-1 rounded mx-1'}>{'{{primeiroNome}}'}</code>
                                <code className={theme === 'light' ? 'bg-blue-100 px-1 rounded mx-1' : 'bg-blue-600 text-white px-1 rounded mx-1'}>{'{{telefone}}'}</code>
                                <code className={theme === 'light' ? 'bg-blue-100 px-1 rounded mx-1' : 'bg-blue-600 text-white px-1 rounded mx-1'}>{'{{email}}'}</code>
                                <code className={theme === 'light' ? 'bg-blue-100 px-1 rounded mx-1' : 'bg-blue-600 text-white px-1 rounded mx-1'}>{'{{categoria}}'}</code>
                                <code className={theme === 'light' ? 'bg-blue-100 px-1 rounded mx-1' : 'bg-blue-600 text-white px-1 rounded mx-1'}>{'{{observacoes}}'}</code>
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>Adicione uma ou mais mensagens para serem enviadas em sequência</p>
                          {/* MODIFICADO: Botão + Mensagem adaptado para tema dark */}
                          {/* ANTES: bg-blue-600, hover:bg-blue-700 (cores fixas) */}
                          <button
                            type="button"
                            onClick={() => {
                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                              const newSequence = [...currentSequence, { type: 'text', content: { text: '' } }];
                              setFormData(prev => ({
                                ...prev,
                                messageContent: { sequence: newSequence }
                              }));
                            }}
                            className="px-3 py-2 text-white text-sm rounded-md flex items-center gap-2 transition-colors font-medium"
                            style={{
                              backgroundColor: theme === 'dark' ? '#3b82f6' : '#2563eb'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2563eb' : '#1d4ed8';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3b82f6' : '#2563eb';
                            }}
                          >
                            <span className="text-lg">+</span>
                            Mensagem
                          </button>
                        </div>

                        <div className="space-y-3">
                          {('sequence' in formData.messageContent) && formData.messageContent.sequence.map((item, index) => (
                            <div key={index} className="border rounded-lg p-4 transition-colors" style={{
                              borderColor: 'var(--border-color)',
                              background: theme === 'dark' ? 'rgba(30, 30, 31, 0.5)' : '#ffffff'
                            }}>
                              <div className="flex justify-between items-center mb-3">
                                <span className={`text-sm font-medium ${theme === 'light' ? 'text-gray-600' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>Mensagem {index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                    const newSequence = currentSequence.filter((_, i) => i !== index);
                                    setFormData(prev => ({
                                      ...prev,
                                      messageContent: { sequence: newSequence }
                                    }));
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Remover
                                </button>
                              </div>

                              <div className="space-y-3">
                                {/* MODIFICADO: Select de tipo de mensagem adaptado para tema dark */}
                                {/* ANTES: border-gray-300, bg-white, text-gray-900 (cores fixas) */}
                                <select
                                  value={item.type}
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    let newContent;
                                    switch (newType) {
                                      case 'text':
                                        newContent = { text: '' };
                                        break;
                                      case 'document':
                                        newContent = { url: '', fileName: '' };
                                        break;
                                      case 'openai':
                                        newContent = { model: '', system: '', user: '' };
                                        break;
                                      case 'groq':
                                        newContent = { model: '', system: '', user: '' };
                                        break;
                                      default:
                                        newContent = { url: '', caption: '' };
                                        break;
                                    }

                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                    const newSequence = currentSequence.map((seqItem, i) =>
                                      i === index ? { type: newType, content: newContent } : seqItem
                                    );
                                    setFormData(prev => ({
                                      ...prev,
                                      messageContent: { sequence: newSequence }
                                    }));
                                  }}
                                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                  style={{
                                    backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                    borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                    color: theme === 'dark' ? 'white' : '#1f2937'
                                  }}
                                >
                                  <option value="text">Texto</option>
                                  <option value="image">Imagem</option>
                                  <option value="video">Vídeo</option>
                                  <option value="audio">Áudio</option>
                                  <option value="document">Arquivo</option>
                                  {/* <option value="openai">OpenAI</option> */}
                                  {/* <option value="groq">Groq AI</option> */}
                                </select>

                                {item.type === 'text' && (
                                  <div className="space-y-2">
                                    {/* Checkbox para usar variações */}
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        id={`useVariations-${index}`}
                                        checked={item.content.useVariations || false}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: {
                                                ...seqItem.content,
                                                useVariations: e.target.checked,
                                                variations: e.target.checked ? [''] : undefined,
                                                text: e.target.checked ? undefined : seqItem.content.text
                                              }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                      />
                                      <label htmlFor={`useVariations-${index}`} className={`text-sm font-medium ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                                        Usar variações de texto
                                      </label>
                                    </div>

                                    {item.content.useVariations ? (
                                      /* Modo variações */
                                      <div className="space-y-2">
                                        <label className={`block text-sm font-medium ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                                          Variações de texto
                                        </label>
                                        {(item.content.variations || ['']).map((variation: string, varIndex: number) => (
                                          <div key={varIndex} className="flex gap-2">
                                            <div className="flex-1">
                                              <textarea
                                                id={`variation-${index}-${varIndex}`}
                                                data-variation-index={varIndex}
                                                data-message-index={index}
                                                placeholder={`Variação ${varIndex + 1}... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}`}
                                                value={variation}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations[varIndex] = e.target.value;
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    messageContent: { sequence: newSequence }
                                                  }));
                                                }}
                                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                                style={{
                                                  backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                                  borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                                  color: theme === 'dark' ? 'white' : '#1f2937'
                                                }}
                                                rows={2}
                                              />
                                            </div>
                                            {(item.content.variations || []).length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations.splice(varIndex, 1);
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    messageContent: { sequence: newSequence }
                                                  }));
                                                }}
                                                className="px-2 py-1 text-red-600 hover:text-red-800"
                                                title="Remover variação"
                                              >
                                                ✕
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                const newVariations = [...(seqItem.content.variations || []), ''];
                                                return {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, variations: newVariations }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded hover:bg-blue-200 flex items-center gap-1"
                                        >
                                          <span>+</span>
                                          Nova variação
                                        </button>
                                      </div>
                                    ) : (
                                      /* Modo texto único */
                                      <div>
                                        <textarea
                                          placeholder="Digite sua mensagem... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                          value={item.content.text || ''}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? { ...seqItem, content: { text: e.target.value } } : seqItem
                                            );
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                          style={{
                                            backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                            borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                            color: theme === 'dark' ? 'white' : '#1f2937'
                                          }}
                                          rows={3}
                                        />
                                      </div>
                                    )}

                                    <div className="flex flex-wrap gap-1">
                                      <span className={`text-xs ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>Variáveis disponíveis:</span>
                                      {['{{nome}}', '{{primeiroNome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                        <button
                                          key={variable}
                                          type="button"
                                          onMouseDown={(e) => {
                                            e.preventDefault(); // Previne perda de foco do textarea
                                            
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            if (item.content.useVariations) {
                                              // Detectar qual textarea está com foco ANTES do clique
                                              const focusedElement = document.activeElement as HTMLTextAreaElement;
                                              const focusedVariationIndex = focusedElement?.getAttribute('data-variation-index');
                                              const focusedMessageIndex = focusedElement?.getAttribute('data-message-index');
                                              
                                              // Verificar se o elemento focado pertence a esta mensagem
                                              const isCorrectMessage = focusedMessageIndex === String(index);
                                              
                                              const variations = item.content.variations || [''];
                                              // Se há um textarea focado da mensagem correta, usar seu índice, senão usar o primeiro
                                              const targetIndex = (isCorrectMessage && focusedVariationIndex !== null) ? parseInt(focusedVariationIndex) : 0;

                                              const newSequence = currentSequence.map((seqItem, i) => {
                                                if (i === index) {
                                                  const newVariations = [...variations];
                                                  // Inserir variável na posição do cursor ou ao final
                                                  if (isCorrectMessage && focusedElement && focusedElement.selectionStart !== undefined) {
                                                    const currentValue = newVariations[targetIndex] || '';
                                                    const start = focusedElement.selectionStart;
                                                    const end = focusedElement.selectionEnd;
                                                    newVariations[targetIndex] = currentValue.substring(0, start) + variable + currentValue.substring(end);
                                                    
                                                    // Mover cursor para depois da variável inserida
                                                    setTimeout(() => {
                                                      focusedElement.selectionStart = focusedElement.selectionEnd = start + variable.length;
                                                      focusedElement.focus();
                                                    }, 0);
                                                  } else {
                                                    // Se não há foco ou foco está em outra mensagem, adicionar ao final da primeira variação
                                                    newVariations[targetIndex] = (newVariations[targetIndex] || '') + variable;
                                                    // Focar no campo correto
                                                    setTimeout(() => {
                                                      const targetElement = document.getElementById(`variation-${index}-${targetIndex}`) as HTMLTextAreaElement;
                                                      if (targetElement) {
                                                        targetElement.focus();
                                                        targetElement.selectionStart = targetElement.selectionEnd = targetElement.value.length;
                                                      }
                                                    }, 0);
                                                  }
                                                  return {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, variations: newVariations }
                                                  };
                                                }
                                                return seqItem;
                                              });
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            } else {
                                              // Adicionar ao texto único
                                              const focusedElement = document.activeElement as HTMLTextAreaElement;
                                              const currentText = currentSequence[index]?.content?.text || '';
                                              
                                              let newText = currentText;
                                              if (focusedElement && focusedElement.selectionStart !== undefined) {
                                                const start = focusedElement.selectionStart;
                                                const end = focusedElement.selectionEnd;
                                                newText = currentText.substring(0, start) + variable + currentText.substring(end);
                                                
                                                // Mover cursor para depois da variável inserida
                                                setTimeout(() => {
                                                  focusedElement.selectionStart = focusedElement.selectionEnd = start + variable.length;
                                                  focusedElement.focus();
                                                }, 0);
                                              } else {
                                                newText = currentText + variable;
                                              }
                                              
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? { ...seqItem, content: { text: newText } } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }
                                          }}
                                          className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                        >
                                          {variable}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {item.type === 'openai' && (
                                  <div className="space-y-4">
                                    <div className={`rounded-lg p-4 border ${theme === 'light' ? 'bg-blue-50 border-blue-200' : ''}`} style={{
                                      background: theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : undefined,
                                      borderColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : undefined
                                    }}>
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-blue-600 font-medium">🤖 OpenAI</span>
                                      </div>
                                      <p className={`text-sm ${theme === 'light' ? 'text-blue-600' : 'text-blue-400'}`}>
                                        Configure os parâmetros para gerar mensagens personalizadas usando inteligência artificial
                                      </p>
                                    </div>

                                    <div>
                                      <label className={`block text-sm font-medium mb-1 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                                        Modelo *
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Digite o modelo da OpenAI (ex: gpt-3.5-turbo)"
                                        value={item.content.model || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, model: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                        style={{
                                          backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                          borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                          color: theme === 'dark' ? 'white' : '#1f2937'
                                        }}
                                      />
                                      <div className="mt-2">
                                        <p className={`text-xs mb-1 ${theme === 'light' ? 'text-gray-500' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-secondary)' : undefined }}>Modelos sugeridos:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {['gpt-3.5-turbo', 'gpt-4', 'gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini'].map((model) => (
                                            <button
                                              key={model}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, model: model }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 border border-gray-300"
                                            >
                                              {model}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{primeiroNome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {item.type === 'groq' && (
                                  <div className="space-y-4">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-yellow-600 font-medium">⚡ Groq AI</span>
                                      </div>
                                      <p className="text-sm text-yellow-600">
                                        Configure os parâmetros para gerar mensagens personalizadas usando Groq AI com velocidade ultra-rápida
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Modelo *
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Digite o modelo do Groq (ex: llama-3.1-8b-instant)"
                                        value={item.content.model || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, model: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div className="mt-2">
                                        <p className="text-xs text-gray-500 mb-1">Modelos sugeridos:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {['llama-3.1-8b-instant', 'llama-3.1-70b-versatile', 'llama-3.2-11b-text-preview', 'mixtral-8x7b-32768', 'gemma2-9b-it'].map((model) => (
                                            <button
                                              key={model}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, model: model }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded hover:bg-yellow-200 border border-yellow-300"
                                            >
                                              {model}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{primeiroNome}}', '{{email}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded hover:bg-orange-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {['image', 'video', 'audio', 'document'].includes(item.type) && (
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <label className="block text-sm font-medium text-gray-700">
                                        Arquivo
                                      </label>
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={item.content.useMediaVariations || false}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                return {
                                                  ...seqItem,
                                                  content: {
                                                    ...seqItem.content,
                                                    useMediaVariations: e.target.checked,
                                                    mediaVariations: e.target.checked ? Array.from({ length: 4 }, () => ({ url: '', caption: '', fileName: '' })) : undefined,
                                                    // Limpar URL principal quando ativar variações
                                                    url: e.target.checked ? '' : seqItem.content.url
                                                  }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className={`text-sm ${theme === 'light' ? 'text-gray-600' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>Usar Variações</span>
                                      </label>
                                    </div>

                                    {item.content.useMediaVariations ? (
                                      // Grid horizontal de 4 variações
                                      <div className="grid grid-cols-4 gap-3">
                                        {Array.from({ length: 4 }, (_, varIndex) => {
                                          const mediaVariations = item.content.mediaVariations || [];
                                          const variation = mediaVariations[varIndex] || { url: '', caption: '', fileName: '' };
                                          const hasFile = variation.url;

                                          return (
                                            <div key={varIndex} className="space-y-2">
                                              {/* Header da variação */}
                                              <div className="flex items-center justify-between">
                                                <span className={`text-xs font-medium ${theme === 'light' ? 'text-gray-600' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                                                  {varIndex === 0 ? 'Principal' : `Var. ${varIndex + 1}`}
                                                </span>
                                                {hasFile && varIndex > 0 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                      const newSequence = currentSequence.map((seqItem, i) => {
                                                        if (i === index) {
                                                          const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                          newVariations[varIndex] = { url: '', caption: '', fileName: '' };
                                                          return {
                                                            ...seqItem,
                                                            content: { ...seqItem.content, mediaVariations: newVariations }
                                                          };
                                                        }
                                                        return seqItem;
                                                      });
                                                      setFormData(prev => ({
                                                        ...prev,
                                                        messageContent: { sequence: newSequence }
                                                      }));
                                                    }}
                                                    className="text-red-500 hover:text-red-700 text-xs p-0.5"
                                                    title="Remover"
                                                  >
                                                    ✕
                                                  </button>
                                                )}
                                              </div>

                                              {/* Preview quadrado */}
                                              <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                                                {!hasFile ? (
                                                  <>
                                                    <input
                                                      type="file"
                                                      id={`file-upload-${index}-${varIndex}`}
                                                      className="hidden"
                                                      accept={
                                                        item.type === 'image' ? 'image/*' :
                                                        item.type === 'video' ? 'video/*' :
                                                        item.type === 'audio' ? 'audio/*' :
                                                        'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                                      }
                                                      onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                          handleFileUpload(file, index, varIndex);
                                                        }
                                                      }}
                                                      disabled={uploadingFiles[`${index}-${varIndex}`]}
                                                    />
                                                    <label
                                                      htmlFor={`file-upload-${index}-${varIndex}`}
                                                      className={`w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                        uploadingFiles[`${index}-${varIndex}`]
                                                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                                          : 'text-gray-500 hover:text-blue-600'
                                                      }`}
                                                    >
                                                      {uploadingFiles[`${index}-${varIndex}`] ? (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                          <span className="text-xs">Enviando</span>
                                                        </div>
                                                      ) : (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="text-2xl">
                                                            {item.type === 'image' && '🖼️'}
                                                            {item.type === 'video' && '🎥'}
                                                            {item.type === 'audio' && '🎵'}
                                                            {item.type === 'document' && '📄'}
                                                          </div>
                                                          <span className="text-xs text-center">
                                                            Fazer upload
                                                          </span>
                                                        </div>
                                                      )}
                                                    </label>
                                                  </>
                                                ) : (
                                                  <div className="w-full h-full relative group">
                                                    {item.type === 'image' && (
                                                      <img
                                                        src={variation.url}
                                                        alt={`Variação ${varIndex + 1}`}
                                                        className="w-full h-full object-cover"
                                                      />
                                                    )}

                                                    {item.type === 'video' && (
                                                      <div className="w-full h-full relative">
                                                        <video
                                                          src={variation.url}
                                                          className="w-full h-full object-cover"
                                                          muted
                                                        />
                                                        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                                          <div className="bg-white bg-opacity-90 p-1 rounded-full">
                                                            <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                                                              <path d="M8 5v14l11-7z"/>
                                                            </svg>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    )}

                                                    {item.type === 'audio' && (
                                                      <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200 flex flex-col items-center justify-center">
                                                        <div className="text-3xl mb-1">🎵</div>
                                                        <div className={`text-xs text-center px-1 ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                                                          Áudio
                                                        </div>
                                                      </div>
                                                    )}

                                                    {item.type === 'document' && (
                                                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex flex-col items-center justify-center p-2">
                                                        <div className="text-3xl mb-1">📄</div>
                                                        <div className={`text-xs text-center truncate w-full ${theme === 'light' ? 'text-gray-700' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }}>
                                                          {variation.fileName || 'Documento'}
                                                        </div>
                                                      </div>
                                                    )}

                                                    {/* Overlay de ações */}
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                      <a
                                                        href={variation.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="bg-white bg-opacity-90 px-2 py-1 rounded text-xs font-medium"
                                                      >
                                                        Ver
                                                      </a>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>

                                              {/* Campo de legenda para imagem e vídeo */}
                                              {hasFile && ['image', 'video'].includes(item.type) && (
                                                <div className="space-y-1">
                                                  <textarea
                                                    placeholder="Legenda..."
                                                    value={variation.caption || ''}
                                                    rows={1}
                                                    onChange={(e) => {
                                                      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                      const newSequence = currentSequence.map((seqItem, i) => {
                                                        if (i === index) {
                                                          const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                          // Garantir que o array tenha o tamanho necessário
                                                          while (newVariations.length <= varIndex) {
                                                            newVariations.push({ url: '', caption: '', fileName: '' });
                                                          }
                                                          newVariations[varIndex] = { ...newVariations[varIndex], caption: e.target.value };
                                                          return {
                                                            ...seqItem,
                                                            content: { ...seqItem.content, mediaVariations: newVariations }
                                                          };
                                                        }
                                                        return seqItem;
                                                      });
                                                      setFormData(prev => ({
                                                        ...prev,
                                                        messageContent: { sequence: newSequence }
                                                      }));
                                                    }}
                                                    onInput={(e) => {
                                                      const target = e.target as HTMLTextAreaElement;
                                                      target.style.height = 'auto';
                                                      target.style.height = target.scrollHeight + 'px';
                                                    }}
                                                    className="w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none overflow-hidden transition-colors"
                                                    style={{
                                                      backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                                      borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                                      color: theme === 'dark' ? 'white' : '#1f2937'
                                                    }}
                                                  />
                                                  {/* Botões de Variáveis */}
                                                  <div className="flex flex-wrap gap-0.5">
                                                    {['{{nome}}', '{{telefone}}', '{{email}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                                      <button
                                                        key={variable}
                                                        type="button"
                                                        onClick={() => {
                                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                          const newSequence = currentSequence.map((seqItem, i) => {
                                                            if (i === index) {
                                                              const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                              while (newVariations.length <= varIndex) {
                                                                newVariations.push({ url: '', caption: '', fileName: '' });
                                                              }
                                                              const currentCaption = newVariations[varIndex]?.caption || '';
                                                              newVariations[varIndex] = { 
                                                                ...newVariations[varIndex], 
                                                                caption: currentCaption + variable 
                                                              };
                                                              return {
                                                                ...seqItem,
                                                                content: { ...seqItem.content, mediaVariations: newVariations }
                                                              };
                                                            }
                                                            return seqItem;
                                                          });
                                                          setFormData(prev => ({
                                                            ...prev,
                                                            messageContent: { sequence: newSequence }
                                                          }));
                                                        }}
                                                        className="px-1 py-0.5 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                                        title={`Adicionar ${variable}`}
                                                      >
                                                        {variable}
                                                      </button>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}

                                              {/* Nome do arquivo para não-imagens */}
                                              {hasFile && !['image', 'video'].includes(item.type) && (
                                                <div className={`text-xs truncate ${theme === 'light' ? 'text-gray-600' : ''}`} style={{ color: theme === 'dark' ? 'var(--text-primary)' : undefined }} title={variation.fileName}>
                                                  {variation.fileName || 'Arquivo'}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      // Interface original de arquivo único
                                      <>
                                        {!item.content.url ? (
                                          <div className="flex items-center gap-3">
                                            <input
                                              type="file"
                                              id={`file-upload-${index}`}
                                              className="hidden"
                                              accept={
                                                item.type === 'image' ? 'image/*' :
                                                item.type === 'video' ? 'video/*' :
                                                item.type === 'audio' ? 'audio/*' :
                                                'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                              }
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                  handleFileUpload(file, index);
                                                }
                                              }}
                                              disabled={uploadingFiles[index]}
                                            />
                                            <label
                                              htmlFor={`file-upload-${index}`}
                                              className={`flex-1 cursor-pointer px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg text-center text-sm font-medium transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                uploadingFiles[index]
                                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                                  : 'bg-white text-gray-700'
                                              }`}
                                            >
                                              {uploadingFiles[index] ? (
                                                <div className="flex flex-col items-center gap-2">
                                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                  <span>Enviando arquivo...</span>
                                                </div>
                                              ) : (
                                                <div className="flex flex-col items-center gap-2">
                                                  <div className="text-2xl">📁</div>
                                                  <span>Clique para fazer upload do arquivo</span>
                                                  <span className="text-xs text-gray-500">
                                                    {item.type === 'image' && 'Imagens: JPG, PNG, GIF, WebP'}
                                                    {item.type === 'video' && 'Vídeos: MP4, AVI, MOV, WMV, MKV'}
                                                    {item.type === 'audio' && 'Áudios: MP3, WAV, OGG, AAC, M4A'}
                                                    {item.type === 'document' && 'Documentos: PDF, DOC, XLS, TXT, ZIP'}
                                                  </span>
                                                </div>
                                              )}
                                            </label>
                                          </div>
                                        ) : (
                                          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-3">
                                                {item.type === 'image' && (
                                                  <img
                                                    src={item.content.url}
                                                    alt="Preview"
                                                    className="w-12 h-12 object-cover rounded"
                                                  />
                                                )}
                                                {item.type !== 'image' && (
                                                  <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center text-2xl">
                                                    {getFileIcon(fileInfos[index]?.type || item.type)}
                                                  </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-sm font-medium text-gray-900 truncate">
                                                    {fileInfos[index]?.name || 'Arquivo carregado'}
                                                  </div>
                                                  <div className="text-xs text-gray-500">
                                                    {fileInfos[index]?.size ? formatFileSize(fileInfos[index].size) : ''}
                                                  </div>
                                                </div>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Remover arquivo"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}

                                    {['image', 'video'].includes(item.type) && !item.content.useMediaVariations && (
                                      <div className="space-y-2">
                                        <input
                                          type="text"
                                          placeholder="Legenda (opcional) - Use variáveis como {{nome}}, {{telefone}}, etc."
                                          value={item.content.caption || ''}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? {
                                                ...seqItem,
                                                content: { ...seqItem.content, caption: e.target.value }
                                              } : seqItem
                                            );
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                          style={{
                                            backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                                            borderColor: theme === 'dark' ? '#3F3F46' : '#d1d5db',
                                            color: theme === 'dark' ? 'white' : '#1f2937'
                                          }}
                                        />
                                        <div className="flex flex-wrap gap-1">
                                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Variáveis:</span>
                                          {['{{nome}}', '{{telefone}}', '{{email}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                            <button
                                              key={variable}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const currentCaption = currentSequence[index]?.content?.caption || '';
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, caption: currentCaption + variable }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                            >
                                              {variable}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {item.type === 'document' && !item.content.useMediaVariations && (
                                      <input
                                        type="text"
                                        placeholder="Nome do arquivo (opcional)"
                                        value={item.content.fileName || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, fileName: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {(!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) && (
                            <div
                              className="text-center py-12 rounded-lg border-2 border-dashed transition-colors"
                              style={{
                                backgroundColor: theme === 'dark' ? '#1a1a1b' : '#f9fafb',
                                borderColor: theme === 'dark' ? '#3f3f46' : '#d1d5db',
                                color: theme === 'dark' ? 'var(--text-secondary)' : '#6b7280'
                              }}
                            >
                              <div className="text-4xl mb-4">📝</div>
                              <p className="text-lg font-medium" style={{ color: theme === 'dark' ? 'var(--text-primary)' : '#374151' }}>Nenhuma mensagem na sequência</p>
                              <p className="text-sm" style={{ color: theme === 'dark' ? 'var(--text-secondary)' : '#6b7280' }}>Clique em "Adicionar Mensagem" para começar a criar sua campanha.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* MODIFICADO: Footer com botões adaptados para tema dark */}
                {/* ANTES: bg-gray-100, text-gray-700, bg-gradient-to-r from-blue-600 to-purple-600 (cores fixas) */}
                <div
                  className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-8 transition-colors border-t"
                  style={{ borderColor: 'var(--border-color)' }}
                >
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="w-full px-6 py-3 rounded-lg transition-colors font-medium"
                    style={{
                      backgroundColor: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2f' : '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-card)';
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-lg transition-all font-medium shadow-lg text-white"
                    style={{
                      backgroundColor: 'var(--primary-button)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--primary-button-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--primary-button)';
                    }}
                  >
                    Criar Campanha
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Relatórios */}
        {/* Renderizar componente de relatório baseado no tipo */}
        {showReportModal && currentReportCampaignId && currentReportCampaignType === 'OFFICIAL_API' && (
          <OfficialApiCampaignReport
            campaignId={currentReportCampaignId}
            onClose={() => setShowReportModal(false)}
          />
        )}

        {/* Modal de relatório WhatsApp Web (mantém original) */}
        {showReportModal && currentReportCampaignType === 'WHATS_WEB' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div ref={whatsWebReportRef} className="rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto relative transition-colors"
                 style={{
                   backgroundColor: theme === 'dark' ? '#1D1E24' : 'white'
                 }}>
              <div className="flex justify-between items-center p-6 border-b transition-colors" style={{
                borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
              }}>
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-semibold text-n-slate-12">Relatório da Campanha</h3>
                  <button
                    onClick={handleRefreshReport}
                    disabled={reportLoading}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Atualizar relatório"
                  >
                    <svg className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {reportLoading ? 'Atualizando...' : 'Atualizar'}
                  </button>
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="transition-colors"
                  style={{ color: theme === 'dark' ? '#9ca3af' : '#9ca3af' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme === 'dark' ? 'white' : '#6b7280';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme === 'dark' ? '#9ca3af' : '#9ca3af';
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                {reportLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Carregando relatório...</span>
                  </div>
                ) : reportData ? (
                  <div className="space-y-6">
                    {/* Informações da Campanha */}
                    <div className="rounded-lg p-4 transition-colors" style={{
                      backgroundColor: theme === 'dark' ? '#2A2A2A' : '#f9fafb'
                    }}>
                      <h4 className="text-lg font-semibold mb-3" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{reportData.campaign.nome}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Status:</span>
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reportData.campaign.status)}`}>
                            {getStatusText(reportData.campaign.status)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Criado por:</span>
                          <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>
                            {reportData.campaign.createdByName || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Data de Criação:</span>
                          <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{new Date(reportData.campaign.criadoEm).toLocaleString('pt-BR')}</span>
                        </div>
                        <div>
                          <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Data de Início:</span>
                          <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>
                            {reportData.campaign.startedAt ? new Date(reportData.campaign.startedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>Data de Conclusão:</span>
                          <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>
                            {reportData.campaign.completedAt ? new Date(reportData.campaign.completedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                        {reportData.campaign.vinculoName && (
                          <div>
                            <span className="font-medium" style={{ color: theme === 'dark' ? '#d1d5db' : '#374151' }}>
                              {reportData.campaign.vinculoTipo === 'AGENTE' ? '🤖 Agente de IA:' : '🏷️ Bot:'}
                            </span>
                            <span className="ml-2" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>
                              {reportData.campaign.vinculoName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Estatísticas */}
                    <div>
                      <h4 className="text-lg font-semibold mb-3" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>Estatísticas de Envio</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="rounded-lg p-4 text-center transition-colors" style={{
                          backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff'
                        }}>
                          <div className="text-2xl font-bold text-blue-600">{reportData.stats.total}</div>
                          <div className="text-sm" style={{ color: theme === 'dark' ? '#93c5fd' : '#1e40af' }}>Total de Contatos</div>
                        </div>
                        <div className="rounded-lg p-4 text-center transition-colors" style={{
                          backgroundColor: theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : '#f0fdf4'
                        }}>
                          <div className="text-2xl font-bold text-green-600">{reportData.stats.sent}</div>
                          <div className="text-sm" style={{ color: theme === 'dark' ? '#86efac' : '#166534' }}>Mensagens Enviadas</div>
                        </div>
                        <div className="rounded-lg p-4 text-center transition-colors" style={{
                          backgroundColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2'
                        }}>
                          <div className="text-2xl font-bold text-red-600">{reportData.stats.failed}</div>
                          <div className="text-sm" style={{ color: theme === 'dark' ? '#fca5a5' : '#dc2626' }}>Mensagens Falharam</div>
                        </div>
                        <div className="rounded-lg p-4 text-center transition-colors" style={{
                          backgroundColor: theme === 'dark' ? 'rgba(234, 179, 8, 0.1)' : '#fefce8'
                        }}>
                          <div className="text-2xl font-bold text-yellow-600">{reportData.stats.pending}</div>
                          <div className="text-sm" style={{ color: theme === 'dark' ? '#fde047' : '#ca8a04' }}>Mensagens Pendentes</div>
                        </div>
                      </div>
                    </div>

                    {/* Mensagens por Sessão */}
                    {Object.keys(reportData.messagesBySession).length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>Distribuição por Sessão</h4>
                        <div className="border rounded-lg overflow-hidden transition-colors" style={{
                          backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                          borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
                        }}>
                          <table className="min-w-full divide-y divide-[var(--border-color-soft)] transition-colors">
                            <thead className="transition-colors" style={{
                              backgroundColor: theme === 'dark' ? '#374151' : '#f9fafb'
                            }}>
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Sessão</th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Total</th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Enviadas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>Falharam</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border-color-soft)] transition-colors" style={{
                              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white'
                            }}>
                              {Object.entries(reportData.messagesBySession).map(([sessionName, sessionData]) => {
                                const messages = (sessionData as any).messages || [];
                                return (
                                  <tr key={sessionName} className="transition-colors" style={{
                                    '--hover-bg': theme === 'dark' ? '#374151' : '#f9fafb'
                                  } as React.CSSProperties}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#374151' : '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{sessionName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{messages.length}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                      {messages.filter((m: any) => m.status === 'SENT').length}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                      {messages.filter((m: any) => m.status === 'FAILED').length}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Mensagens Detalhadas */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-lg font-semibold" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>Detalhes das Mensagens</h4>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleDownloadReport}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                          >
                            📊 Download CSV
                          </button>
                          <button
                            disabled={exportingPdf}
                            onClick={async () => {
                              const el = whatsWebReportRef.current;
                              if (!el || !reportData || exportingPdf) return;
                              setExportingPdf(true);
                              const toastId = 'pdf-export';
                              toast.loading('Gerando PDF...', { id: toastId });
                              try {
                                await exportToPdf(el, `relatorio-${reportData.campaign.nome}.pdf`);
                                toast.success('PDF gerado com sucesso!', { id: toastId });
                              } catch (err) {
                                console.error('Erro ao gerar PDF:', err);
                                toast.error('Erro ao gerar PDF', { id: toastId });
                              } finally {
                                setExportingPdf(false);
                              }
                            }}
                            className={`px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 ${exportingPdf ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {exportingPdf ? 'Gerando...' : '📄 Download PDF'}
                          </button>
                        </div>
                      </div>

                      {(() => {
                        const messages = reportData.campaign.messages || [];
                        const totalPages = Math.ceil(messages.length / reportItemsPerPage);
                        const startIndex = (reportCurrentPage - 1) * reportItemsPerPage;
                        const endIndex = startIndex + reportItemsPerPage;
                        const currentMessages = messages.slice(startIndex, endIndex);

                        return (
                          <>
                            <div className="border rounded-lg overflow-x-auto transition-colors" style={{
                              backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white',
                              borderColor: theme === 'dark' ? '#3F3F46' : '#e5e7eb'
                            }}>
                              <table className="w-full divide-y divide-[var(--border-color-soft)] transition-colors">
                                <thead className="transition-colors" style={{
                                  backgroundColor: theme === 'dark' ? '#374151' : '#f9fafb'
                                }}>
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', width: '20%' }}>Nome</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', width: '15%' }}>Telefone</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', width: '10%' }}>Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', width: '15%' }}>Sessão</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', width: '15%' }}>Data de Envio</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', width: '10%' }}>Erro</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-[var(--border-color-soft)] transition-colors" style={{
                                  backgroundColor: theme === 'dark' ? '#2A2A2A' : 'white'
                                }}>
                                  {currentMessages.map((message: any) => (
                                    <tr key={message.id} className="transition-colors" style={{
                                      '--hover-bg': theme === 'dark' ? '#374151' : '#f9fafb'
                                    } as React.CSSProperties}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#374151' : '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}>
                                      <td className="px-4 py-4 text-sm break-words" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{message.contactName}</td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{message.contactPhone}</td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          message.status === 'SENT' ? 'bg-green-100 text-green-800' :
                                          message.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                          'bg-yellow-100 text-yellow-800'
                                        }`}>
                                          {message.status === 'SENT' ? 'Enviada' :
                                           message.status === 'FAILED' ? 'Falhou' : 'Pendente'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>{message.sessionName || 'N/A'}</td>
                                      <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: theme === 'dark' ? 'white' : '#111827' }}>
                                        {message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A'}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap">
                                        {message.errorMessage && message.errorMessage !== 'N/A' ? (
                                          (() => {
                                            const errorInfo = getErrorIcon(message.errorMessage);
                                            return errorInfo ? (
                                              <div className="group relative inline-flex items-center">
                                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${errorInfo.bgColor} cursor-help`}>
                                                  <span className="text-lg">{errorInfo.icon}</span>
                                                </div>

                                                {/* Tooltip com posicionamento inteligente */}
                                                <div className="group-hover:block hidden absolute z-[9999]">
                                                  {/* Versão à direita */}
                                                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700 group-hover/right:block hidden">
                                                    {/* Seta esquerda */}
                                                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-l border-b border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Versão à esquerda (fallback) */}
                                                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700">
                                                    {/* Seta direita */}
                                                    <div className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-r border-t border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <span className="text-sm text-red-600">
                                                {message.errorMessage}
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          <span className="text-sm text-gray-400 flex items-center justify-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                                              <span className="text-lg">✅</span>
                                            </div>
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {messages.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                  Nenhuma mensagem encontrada
                                </div>
                              )}
                            </div>

                            {/* Paginação */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between mt-4">
                                <div className="text-sm text-gray-700">
                                  Mostrando {startIndex + 1} a {Math.min(endIndex, messages.length)} de {messages.length} mensagens
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.max(page - 1, 1))}
                                    disabled={reportCurrentPage === 1}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Anterior
                                  </button>

                                  <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                      <button
                                        key={pageNum}
                                        onClick={() => setReportCurrentPage(pageNum)}
                                        className={`px-3 py-2 text-sm rounded-md ${
                                          pageNum === reportCurrentPage
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                      >
                                        {pageNum}
                                      </button>
                                    ))}
                                  </div>

                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.min(page + 1, totalPages))}
                                    disabled={reportCurrentPage === totalPages}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Próximo
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Erro ao carregar relatório
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmação de Exclusão */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{
              background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : '#ffffff'
            }}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4 text-n-slate-12">
                  Confirmar Exclusão
                </h3>
                <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Tem certeza que deseja excluir esta campanha?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelDeleteCampaign}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmDeleteCampaign}
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                    style={{
                      backgroundColor: '#dc2626'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmação de Pausa */}
        {showPauseConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{
              background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : '#ffffff'
            }}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4 text-n-slate-12">
                  Pausar Campanha
                </h3>
                <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Deseja pausar esta campanha? Os envios serão interrompidos e você poderá retomá-la depois.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelToggleCampaign}
                    disabled={!!togglingCampaign}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmToggleCampaign}
                    disabled={!!togglingCampaign}
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    style={{
                      backgroundColor: '#d97706'
                    }}
                    onMouseEnter={(e) => !togglingCampaign && (e.currentTarget.style.backgroundColor = '#b45309')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#d97706')}
                  >
                    {togglingCampaign ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Pausando...
                      </>
                    ) : (
                      'Pausar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmação de Retomada */}
        {showResumeConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="rounded-lg shadow-xl max-w-md w-full mx-4" style={{
              background: theme === 'dark' ? 'linear-gradient(135deg, #1e1e1f 0%, #252527 100%)' : '#ffffff'
            }}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4 text-n-slate-12">
                  Retomar Campanha
                </h3>
                <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Deseja retomar esta campanha? Os envios continuarão de onde pararam.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelToggleCampaign}
                    disabled={!!togglingCampaign}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f3f4f6',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmToggleCampaign}
                    disabled={!!togglingCampaign}
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    style={{
                      backgroundColor: '#059669'
                    }}
                    onMouseEnter={(e) => !togglingCampaign && (e.currentTarget.style.backgroundColor = '#047857')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                  >
                    {togglingCampaign ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Retomando...
                      </>
                    ) : (
                      'Retomar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}